import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ImageViewer.css';

const ImageViewer = ({ src, alt, onClose }) => {
    const { t } = useTranslation();
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const viewerRef = useRef(null);

    // Close on Escape key press and lock scroll
    useEffect(() => {
        if (!src) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [onClose, src]);

    // Fix for "Unable to preventDefault inside passive event listener invocation"
    useEffect(() => {
        const node = viewerRef.current;
        if (!node) return;

        const handleWheelValues = (e) => {
            e.preventDefault();
            const delta = e.deltaY * -0.01;
            const newScale = Math.min(Math.max(1, scale + delta), 4); // Limit zoom between 1x and 4x

            // If zooming out to 1, reset position
            if (newScale === 1) {
                setPosition({ x: 0, y: 0 });
            }

            setScale(newScale);
        };

        // React's onWheel is passive by default in some versions/browsers for scrollable elements,
        // but manually attaching with { passive: false } allows preventDefault.
        node.addEventListener('wheel', handleWheelValues, { passive: false });

        return () => {
            node.removeEventListener('wheel', handleWheelValues);
        };
    }, [scale]); // Re-bind when scale changes to access latest state

    // Reset zoom/pan when src changes
    useEffect(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, [src]);

    const handleMouseDown = (e) => {
        if (scale > 1) {
            e.preventDefault();
            setIsDragging(true);
            dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            e.preventDefault();
            const newX = e.clientX - dragStart.current.x;
            const newY = e.clientY - dragStart.current.y;
            setPosition({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch support
    const handleTouchStart = (e) => {
        if (scale > 1 && e.touches.length === 1) {
            // Don't prevent default here immediately or clicks might fail, 
            // but for panning we generally want to stop scroll.
            // e.preventDefault(); 
            setIsDragging(true);
            const touch = e.touches[0];
            dragStart.current = { x: touch.clientX - position.x, y: touch.clientY - position.y };
        }
    };

    const handleTouchMove = (e) => {
        if (isDragging && scale > 1 && e.touches.length === 1) {
            e.preventDefault(); // Stop page scroll
            const touch = e.touches[0];
            const newX = touch.clientX - dragStart.current.x;
            const newY = touch.clientY - dragStart.current.y;
            setPosition({ x: newX, y: newY });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    if (!src) return null;

    return (
        <div
            className="image-viewer-backdrop"
            onClick={onClose}
            ref={viewerRef}
        >
            <button className="image-viewer-close" onClick={onClose}>
                &times;
            </button>

            <div
                className="image-viewer-container"
                onClick={(e) => e.stopPropagation()}
                style={{
                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <img
                    src={src}
                    alt={alt || t('enlarged_view')}
                    className="image-viewer-img"
                    draggable={false}
                />
            </div>

            <div className="zoom-controls" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setScale(Math.min(scale + 0.5, 4))}>+</button>
                <div className="zoom-level">{Math.round(scale * 100)}%</div>
                <button onClick={() => setScale(Math.max(1, scale - 0.5))}>-</button>
            </div>
        </div>
    );
};

export default ImageViewer;
